import type {TalentPoolActions} from 'src/action-creators/talent-pools/talent-pools';
import type {
  TalentPoolListSuggestion,
  TalentPoolList,
  TalentPoolUsersResponse,
  TalentPoolFolder,
  TalentPoolCandidate,
  TalentPoolHierarchy,
  TalentPoolWorkflowFolders,
  TalentPoolStatusGroup,
} from 'src/types/talent-pools';
import {
  TALENT_POOL_LIST,
  TALENT_POOL_LIST_ERROR,
  TALENT_POOL_USERS_SEARCH,
  TALENT_POOL_USERS_SEARCH_ERROR,
  TALENT_POOL_DETAILS,
  TALENT_POOL_DETAILS_ERROR,
  TALENT_POOL_FOLDERS,
  TALENT_POOL_FOLDERS_ERROR,
  TALENT_POOL_FOLDER_DETAILS,
  TALENT_POOL_FOLDER_DETAILS_ERROR,
  TALENT_POOL_FOLDERS_HIERARCHY,
  TALENT_POOL_FOLDERS_HIERARCHY_ERROR,
  TALENT_POOL_FOLDERS_WORKFLOW_ERROR,
  TALENT_POOL_FOLDERS_WORKFLOW,
  TALENT_POOL_STATUS_GROUPS,
  TALENT_POOL_STATUS_GROUPS_ERROR,
  TALENT_POOL_STATUS_GROUP_DELETE,
  TALENT_POOL_STATUS_GROUP_DELETE_ERROR,
  TALENT_POOL_STATUS_GROUP_CREATE,
  TALENT_POOL_STATUS_GROUP_CREATE_ERROR,
  TALENT_POOL_STATUS_GROUP_UPDATE,
  TALENT_POOL_STATUS_GROUP_UPDATE_ERROR,
} from 'src/action-creators/talent-pools/talent-pools';
import {
  TALENT_POOL_LIST_SUGGESTION,
  TALENT_POOL_LIST_SUGGESTION_ERROR,
} from 'src/action-creators/talent-pools/talent-pool-suggestion';
import {
  TALENT_POOL_CANDIDATE,
  TALENT_POOL_CANDIDATE_ERROR,
  TRM_APPLICANTS,
  TRM_APPLICANTS_ERROR,
  TALENT_POOL_UPDATE_CANDIDATE_STATUS,
  TALENT_POOL_UPDATE_CANDIDATE_STATUS_ERROR,
} from 'src/action-creators/talent-pools/candidate';


export type TalentPoolsState = {
  talentPoolListSuggestion: TalentPoolListSuggestion | null | undefined;
  talentPoolList: TalentPoolList | null | undefined;
  talentPoolUsers: TalentPoolUsersResponse | null | undefined;
  talentPoolDetails: TalentPoolFolder | null | undefined;
  talentPoolFolders: TalentPoolList | null | undefined;
  talentPoolCandidate: TalentPoolCandidate | null | undefined;
  trmApplicants: TalentPoolCandidate | null | undefined;
  talentPoolFolderDetails: TalentPoolFolder | null | undefined;
  talentPoolFoldersHierarchy: TalentPoolHierarchy | null | undefined;
  talentPoolFoldersWorkflow: TalentPoolWorkflowFolders | null | undefined;
  talentPoolStatusGroups: Array<TalentPoolStatusGroup> | null | undefined;
};

const initialState: TalentPoolsState = {
  talentPoolListSuggestion: null,
  talentPoolList: null,
  talentPoolUsers: null,
  talentPoolDetails: null,
  talentPoolFolders: null,
  talentPoolCandidate: null,
  trmApplicants: null,
  talentPoolFolderDetails: null,
  talentPoolFoldersHierarchy: null,
  talentPoolFoldersWorkflow: null,
  talentPoolStatusGroups: null,
};

export default function talentPoolsReducer(
  state: TalentPoolsState = initialState,
  action: TalentPoolActions,
): TalentPoolsState {
  switch (action.type) {
    case TALENT_POOL_LIST_SUGGESTION:
      return {...state, talentPoolListSuggestion: action.payload};
    case TALENT_POOL_LIST_SUGGESTION_ERROR:
      return {
        ...state,
        talentPoolListSuggestion: null,
      };
    case TALENT_POOL_LIST:
      return {...state, talentPoolList: action.payload};
    case TALENT_POOL_LIST_ERROR:
      return {
        ...state,
        talentPoolList: null,
      };
    case TALENT_POOL_USERS_SEARCH:
      return {...state, talentPoolUsers: action.payload};
    case TALENT_POOL_USERS_SEARCH_ERROR:
      return {
        ...state,
        talentPoolUsers: null,
      };
    case TALENT_POOL_DETAILS:
      return {...state, talentPoolDetails: action.payload};
    case TALENT_POOL_DETAILS_ERROR:
      return {
        ...state,
        talentPoolDetails: null,
      };
    case TALENT_POOL_FOLDERS:
      return {...state, talentPoolFolders: action.payload};
    case TALENT_POOL_FOLDERS_ERROR:
      return {
        ...state,
        talentPoolFolders: null,
      };
    case TALENT_POOL_CANDIDATE:
      return {
        ...state,
        talentPoolCandidate: action.payload,
      };
    case TALENT_POOL_CANDIDATE_ERROR:
      return {
        ...state,
        talentPoolCandidate: null,
      };
    case TRM_APPLICANTS:
      return {...state, trmApplicants: action.payload};
    case TRM_APPLICANTS_ERROR:
      return {
        ...state,
        trmApplicants: null,
      };
    case TALENT_POOL_FOLDER_DETAILS:
      return {...state, talentPoolFolderDetails: action.payload};
    case TALENT_POOL_FOLDER_DETAILS_ERROR:
      return {
        ...state,
        talentPoolFolderDetails: null,
      };
    case TALENT_POOL_FOLDERS_HIERARCHY:
      return {...state, talentPoolFoldersHierarchy: action.payload};
    case TALENT_POOL_FOLDERS_HIERARCHY_ERROR:
      return {
        ...state,
        talentPoolFoldersHierarchy: null,
      };
    case TALENT_POOL_FOLDERS_WORKFLOW:
      return {...state, talentPoolFoldersWorkflow: action.payload};
    case TALENT_POOL_FOLDERS_WORKFLOW_ERROR:
      return {
        ...state,
        talentPoolFoldersWorkflow: null,
      };

    // talent pool status groups
    case TALENT_POOL_STATUS_GROUPS:
      return {...state, talentPoolStatusGroups: action.payload};
    case TALENT_POOL_STATUS_GROUPS_ERROR:
      return {
        ...state,
        talentPoolStatusGroups: null,
      };
    case TALENT_POOL_STATUS_GROUP_DELETE:
      return {
        ...state,
        talentPoolStatusGroups: state.talentPoolStatusGroups?.filter(
          (statusGroup) => statusGroup.id !== action.payload,
        ),
      };
    case TALENT_POOL_STATUS_GROUP_CREATE:
      return {
        ...state,
        talentPoolStatusGroups: [
          action.payload,
          ...(state.talentPoolStatusGroups || []),
        ],
      };
    case TALENT_POOL_STATUS_GROUP_UPDATE:
      return {
        ...state,
        talentPoolStatusGroups: state.talentPoolStatusGroups?.map(
          (statusGroup) =>
            statusGroup.id === action.payload.id ? action.payload : statusGroup,
        ),
      };
    case TALENT_POOL_UPDATE_CANDIDATE_STATUS: {
      if (!state.talentPoolCandidate) {
        return state;
      }
      return {
        ...state,
        talentPoolCandidate: {
          ...state.talentPoolCandidate,
          // @ts-expect-error - TS2411 - Property 'StatusId' of type 'string' is not assignable to 'string' index type 'string'.
          candidates: state.talentPoolCandidate.candidates.map((candidate) =>
            String(candidate.Id) === String(action.payload.id)
              ? {...candidate, StatusId: action.payload.statusId.toString()}
              : candidate,
          ),
        },
      };
    }
    default:
      return state;
  }
}
